import React, { useState } from "react";
import { requireAuth, useAuth } from "../../util/auth";
import CRMSection from "../../components/Custom/CRMSection";
import EventManager from "../../components/Custom/EventManager";
import LargeCard from "../../components/Custom/LargeCard";
import PostWebinar from "../../components/Custom/PostWebinar";
import PostSystemNotification from "../../components/Custom/PostSystemNotification";
import NotificationTemplateEditor from "../../components/Custom/NotificationTemplateEditor";
import DndManagement from "../../components/Custom/DndManagement";
import { apiRequest } from "../../util/util";

const ADMIN_TABS = [
  {
    id: "events",
    title: "Events & Notifications",
    description: "Calendar, templates, and DND controls.",
  },
  {
    id: "broadcasts",
    title: "Broadcasts",
    description: "Webinar posts and system notifications.",
  },
  {
    id: "migrations",
    title: "Team Migration",
    description: "Prepopulate and migrate team-member data.",
  },
  {
    id: "crm",
    title: "Customers & CRM",
    description: "Customer list, subscriptions, and approvals.",
  },
];

function PrepopulateTeamMembersTool() {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(false);

  const handlePrepopulate = async () => {
    if (!window.confirm("This will prepopulate teamMembers for ALL users with teams. Safe to run multiple times. Continue?")) {
      return;
    }
    setLoading(true);
    setStatus(null);
    try {
      const result = await apiRequest("prepopulate-team-members", "POST", {});
      setStatus({ success: result });
    } catch (err) {
      setStatus({ error: err.message });
    }
    setLoading(false);
  };

  return (
    <div className="space-y-4">
      <button
        onClick={handlePrepopulate}
        disabled={loading}
        className="px-4 py-2 bg-green-600 text-white rounded hover:bg-green-700 disabled:opacity-50"
      >
        {loading ? "Processing..." : "Prepopulate Team Members"}
      </button>
      <p className="text-sm text-gray-500">
        Copies team members to user documents WITHOUT modifying teams. Safe to run before deploying new code.
      </p>
      {status?.error && (
        <div className="p-3 bg-red-100 text-red-700 rounded">{status.error}</div>
      )}
      {status?.success && (
        <div className="p-3 bg-green-100 text-green-700 rounded">
          <pre className="text-sm overflow-auto">{JSON.stringify(status.success, null, 2)}</pre>
        </div>
      )}
    </div>
  );
}

function MigrateTeamsTool() {
  const [userId, setUserId] = useState("");
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(false);

  const handleMigrate = async () => {
    if (!userId.trim()) {
      setStatus({ error: "Please enter a user ID" });
      return;
    }
    setLoading(true);
    setStatus(null);
    try {
      const result = await apiRequest("migrate-teams", "POST", { userId: userId.trim() });
      setStatus({ success: result });
      setUserId("");
    } catch (err) {
      setStatus({ error: err.message });
    }
    setLoading(false);
  };

  const handleMigrateAll = async () => {
    if (!window.confirm("This will migrate ALL users with teams. Are you sure?")) {
      return;
    }
    setLoading(true);
    setStatus(null);
    try {
      const result = await apiRequest("migrate-teams", "POST", { migrateAll: true });
      setStatus({ success: result });
    } catch (err) {
      setStatus({ error: err.message });
    }
    setLoading(false);
  };

  return (
    <div className="space-y-4">
      <div className="flex gap-2">
        <input
          type="text"
          value={userId}
          onChange={(e) => setUserId(e.target.value)}
          placeholder="Enter user ID to migrate"
          className="flex-1 px-3 py-2 border rounded"
        />
        <button
          onClick={handleMigrate}
          disabled={loading}
          className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50"
        >
          {loading ? "Migrating..." : "Migrate"}
        </button>
      </div>
      <div className="border-t pt-4">
        <button
          onClick={handleMigrateAll}
          disabled={loading}
          className="px-4 py-2 bg-orange-600 text-white rounded hover:bg-orange-700 disabled:opacity-50"
        >
          {loading ? "Migrating..." : "Migrate All Users"}
        </button>
        <p className="text-sm text-gray-500 mt-2">This will migrate all users who have teams to the new format.</p>
      </div>
      {status?.error && (
        <div className="p-3 bg-red-100 text-red-700 rounded">{status.error}</div>
      )}
      {status?.success && (
        <div className="p-3 bg-green-100 text-green-700 rounded">
          <pre className="text-sm overflow-auto">{JSON.stringify(status.success, null, 2)}</pre>
        </div>
      )}
    </div>
  );
}

function CRMPage(props) {
  const auth = useAuth();
  const [activeTab, setActiveTab] = useState("events");

  function renderActiveTab() {
    if (activeTab === "events") {
      return (
        <>
          <div className="container">
            <LargeCard titleDescriptions = {[{title: "Event Calendar", description: "Manage recurring and special Zoom events."}]}>
              <EventManager/>
            </LargeCard>
          </div>
          <div className="container">
            <LargeCard titleDescriptions = {[{title: "Notification Templates", description: "Manage email templates for event notification reminders."}]}>
              <NotificationTemplateEditor/>
            </LargeCard>
          </div>
          <div className="container">
            <LargeCard titleDescriptions = {[{title: "Do Not Disturb", description: "Manage users who have opted out of email and/or SMS notifications."}]}>
              <DndManagement/>
            </LargeCard>
          </div>
        </>
      );
    }

    if (activeTab === "broadcasts") {
      return (
        <>
          <div className="container">
            <LargeCard titleDescriptions = {[{title: "Post a Webinar", description: "Here you can post a webinar that was previously done."}]}>
              <PostWebinar/>
            </LargeCard>
          </div>
          <div className="container">
            <LargeCard titleDescriptions = {[{title: "Edit System Notification", description: "Here you can post a system wide notification for all users letting them know of outages."}]}>
              <PostSystemNotification/>
            </LargeCard>
          </div>
        </>
      );
    }

    if (activeTab === "migrations") {
      return (
        <>
          <div className="container">
            <LargeCard titleDescriptions = {[{title: "Prepopulate Team Members (Step 1)", description: "Pre-populate teamMembers on user documents from existing teams. Does NOT modify teams. Run this BEFORE deploying T-020."}]}>
              <PrepopulateTeamMembersTool/>
            </LargeCard>
          </div>
          <div className="container">
            <LargeCard titleDescriptions = {[{title: "Migrate User Teams (Step 2)", description: "Migrate a user's teams to the new teamMembers array format. This soft-deletes teams. Run AFTER T-020 is stable."}]}>
              <MigrateTeamsTool/>
            </LargeCard>
          </div>
        </>
      );
    }

    return <CRMSection/>;
  }

  return (
    <>
      {(auth.user.isAdmin === true)?(
        <>
          <div className="container">
            <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-3 mb-4">
              {ADMIN_TABS.map((tab) => (
                <button
                  key={tab.id}
                  type="button"
                  onClick={() => setActiveTab(tab.id)}
                  className={`text-left rounded-lg border p-4 transition ${
                    activeTab === tab.id
                      ? "border-green-600 bg-green-50"
                      : "border-gray-200 bg-white hover:bg-gray-50"
                  }`}
                >
                  <p className={`text-sm font-semibold ${activeTab === tab.id ? "text-green-700" : "text-gray-800"}`}>
                    {tab.title}
                  </p>
                  <p className="text-xs text-gray-500 mt-1">{tab.description}</p>
                </button>
              ))}
            </div>
          </div>

          {renderActiveTab()}
        </>
      ):(
        <p>Please wait. We are verifying your access to this page.</p>
      )}
    </>
  );
}

export default requireAuth(CRMPage);
